import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import {
  QaiInnerWrapComponent,
  QaiProductTileComponent,
  QaiItemGridComponent,
  QaiButtonComponent,
  QaiSlotDirective,
  QaiLoadingComponent,
} from '@qai/ui';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { IPersonalPracticeSim } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-simulation';
import { getAccountNameByUuid } from 'src/app/utils/local-storage.util';

@Component({
  selector: 'app-personalized-practice-tab',
  templateUrl: './personalized-practice-tab.component.html',
  styleUrls: ['./personalized-practice-tab.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'personalizedPractice',
    },
  ],
  imports: [
    CommonModule,
    TranslocoModule,
    QaiInnerWrapComponent,
    QaiItemGridComponent,
    QaiProductTileComponent,
    QaiButtonComponent,
    QaiSlotDirective,
    QaiLoadingComponent,
  ],
})
export class PersonalizedPracticeTabComponent implements OnInit, OnChanges {
  personalSimulations$!: Observable<IPersonalPracticeSim[]>;
  accountUuid: string = '';
  accountName: string = '';
  isTranslationLoaded: boolean = false;

  @Input() selectedAccount: { label: string; value: string } | null = null;
  @Input() selectedTeams: string[] = [];
  @Input() products: IPersonalPracticeSim[] = [];

  constructor(
    private amplitudeService: AmplitudeService,
    private router: Router,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    Promise.all([
      this.translationService.loadScopedTranslation('reportingV2'),
      this.translationService.loadScopedTranslation('personalizedPractice'),
    ]).then(() => {
      this.isTranslationLoaded = true;
      this.accountUuid = localStorage.getItem('current_account_uuid') || '';
      this.accountName = getAccountNameByUuid(this.accountUuid);
      if (this.products && this.products.length > 0) {
        this.updateSimulations();
      } else {
        this.personalSimulations$ = of([]);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products']) {
      if (this.isTranslationLoaded) {
        this.updateSimulations();
      }
    }
  }

  private updateSimulations(): void {
    this.personalSimulations$ = of(this.enrichSimulations(this.products));
  }

  enrichSimulations(sims: IPersonalPracticeSim[]): IPersonalPracticeSim[] {
    return sims.map((sim) => {
      const ribbonIcon = 'stars';
      const ribbonKind = 'primary';
      const buttonKey = 'VIEW_SESSIONS';
      const sessionKey =
        sim.sessionsCount === 1 ? 'TOTAL_SESSION' : 'TOTAL_SESSIONS';

      return {
        ...sim,
        ribbonIcon,
        ribbonKind,
        buttonText: this.translationService.translate(
          buttonKey,
          'personalizedPractice'
        ),
        description: `${sim.sessionsCount} ${this.translationService.translate(
          sessionKey,
          'personalizedPractice'
        )}`,
      };
    });
  }

  viewSession(simulation: IPersonalPracticeSim): void {
    if (simulation.sessionsCount === 0) {
      return;
    }

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_PERSONALIZED_PRACTICE_TAB,
      {
        product_name: simulation.moduleName,
        account_name: this.accountName,
      }
    );

    this.router.navigate([
      '/reporting_dashboard/personalized-practice',
      simulation.moduleUuid,
    ]);
  }
}
